import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../components/ui/dialog';
import { ScrollArea } from '../../components/ui/scroll-area';
import { useAdminAuth } from '../../context/AdminAuthContext';
import { toast } from 'sonner';
import { 
  Loader2, Mail, Send, Check, Eye, MousePointer, AlertOctagon, 
  XCircle, Clock, RefreshCw, User, Building2, Ticket, ExternalLink,
  MessageSquare, Settings, RotateCcw, FileText
} from 'lucide-react';

var MSG_STATUS_CONFIG = {
  'pending': { className: 'bg-yellow-500/20 text-yellow-400', icon: Clock, label: 'Ausstehend' },
  'sent': { className: 'bg-blue-500/20 text-blue-400', icon: Send, label: 'Gesendet' },
  'delivered': { className: 'bg-green-500/20 text-green-400', icon: Check, label: 'Zugestellt' },
  'opened': { className: 'bg-emerald-500/20 text-emerald-400', icon: Eye, label: 'Geöffnet' },
  'clicked': { className: 'bg-cyan-500/20 text-cyan-400', icon: MousePointer, label: 'Geklickt' },
  'bounced': { className: 'bg-red-500/20 text-red-400', icon: AlertOctagon, label: 'Zurückgewiesen' },
  'failed': { className: 'bg-red-600/20 text-red-500', icon: XCircle, label: 'Fehlgeschlagen' }
};

export var AdminMailLog = function() {
  var auth = useAdminAuth();
  var token = auth.token;
  
  var messagesState = useState([]);
  var messages = messagesState[0];
  var setMessages = messagesState[1];
  
  var statsState = useState({});
  var stats = statsState[0];
  var setStats = statsState[1];
  
  var loadingState = useState(true);
  var loading = loadingState[0];
  var setLoading = loadingState[1];
  
  var refreshingState = useState(false);
  var refreshing = refreshingState[0];
  var setRefreshing = refreshingState[1];
  
  var filterState = useState('all');
  var filter = filterState[0];
  var setFilter = filterState[1];
  
  var autoRefreshRef = useRef(null);
  var backendUrl = process.env.REACT_APP_BACKEND_URL || '';
  
  // Detail dialog state
  var detailDialogState = useState(false);
  var showDetailDialog = detailDialogState[0];
  var setShowDetailDialog = detailDialogState[1];
  
  var selectedMessageState = useState(null);
  var selectedMessage = selectedMessageState[0];
  var setSelectedMessage = selectedMessageState[1];
  
  var loadingDetailState = useState(false);
  var loadingDetail = loadingDetailState[0];
  var setLoadingDetail = loadingDetailState[1];
  
  var resendingState = useState(false);
  var resending = resendingState[0];
  var setResending = resendingState[1];

  var fetchStats = useCallback(function() {
    var url = backendUrl + '/api/admin/mail-log/stats';
    return fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setStats(data);
        });
      }
    }).catch(function(error) {
      console.error('Stats fetch error:', error);
    });
  }, [backendUrl, token]);

  var fetchMessages = useCallback(function(silent) {
    if (!silent) setLoading(true);
    
    var url = backendUrl + '/api/admin/mail-log?limit=100';
    if (filter && filter !== 'all') {
      url += '&status=' + filter;
    }
    
    return fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setMessages(data.messages || []);
        });
      }
    }).catch(function(error) {
      console.error('Messages fetch error:', error);
      if (!silent) toast.error('Fehler beim Laden');
    }).finally(function() {
      if (!silent) setLoading(false);
    });
  }, [backendUrl, token, filter]);

  var handleRefreshAll = function() {
    setRefreshing(true);
    var url = backendUrl + '/api/admin/mail-log?limit=100&update_status=true';
    if (filter && filter !== 'all') {
      url += '&status=' + filter;
    }
    
    fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setMessages(data.messages || []);
          toast.success('Status aktualisiert');
          fetchStats();
        });
      }
    }).catch(function() {
      toast.error('Fehler beim Aktualisieren');
    }).finally(function() {
      setRefreshing(false);
    });
  };
  
  var handleOpenDetail = function(messageId) {
    setLoadingDetail(true);
    setShowDetailDialog(true);
    setSelectedMessage(null);
    
    var url = backendUrl + '/api/admin/mail-log/' + messageId;
    fetch(url, {
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          setSelectedMessage(data);
        });
      } else {
        toast.error('Nachricht nicht gefunden');
        setShowDetailDialog(false);
      }
    }).catch(function() {
      toast.error('Fehler beim Laden');
      setShowDetailDialog(false);
    }).finally(function() {
      setLoadingDetail(false);
    });
  };
  
  var handleResendEmail = function(messageId) {
    setResending(true);
    var url = backendUrl + '/api/admin/mail-log/' + messageId + '/resend';
    
    fetch(url, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      return response.json().then(function(data) {
        if (data.success) {
          toast.success(data.message || 'E-Mail erneut gesendet');
          setShowDetailDialog(false);
          fetchMessages(true);
          fetchStats();
        } else {
          toast.error(data.error || 'Fehler beim Senden');
        }
      });
    }).catch(function() {
      toast.error('Verbindungsfehler');
    }).finally(function() {
      setResending(false);
    });
  };

  var handleRefreshSingle = function(messageId) {
    var url = backendUrl + '/api/admin/mail-log/' + messageId + '/refresh';
    
    fetch(url, {
      method: 'POST',
      headers: { 'Authorization': 'Bearer ' + token }
    }).then(function(response) {
      if (response.ok) {
        return response.json().then(function(data) {
          // Update message in list
          setMessages(function(prev) {
            return prev.map(function(m) {
              if (m.id === messageId) {
                return { ...m, message_status: data.message_status };
              }
              return m;
            });
          });
          toast.success('Status: ' + (MSG_STATUS_CONFIG[data.message_status]?.label || data.message_status));
          fetchStats();
        });
      } else {
        return response.json().then(function(data) {
          toast.error(data.detail || 'Fehler');
        });
      }
    }).catch(function() {
      toast.error('Fehler beim Aktualisieren');
    });
  };

  useEffect(function() {
    fetchMessages(false);
    fetchStats();
  }, [filter]);

  useEffect(function() {
    // Auto-refresh every 30 seconds
    autoRefreshRef.current = setInterval(function() {
      fetchMessages(true);
      fetchStats();
    }, 30000);
    return function() {
      if (autoRefreshRef.current) {
        clearInterval(autoRefreshRef.current);
      }
    };
  }, [fetchMessages, fetchStats]);

  // Stats Cards
  var statsCards = React.createElement('div', { className: 'grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-4 mb-6' },
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'all' ? 'ring-2 ring-blue-500' : ''),
      onClick: function() { setFilter('all'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(Mail, { className: 'h-5 w-5 text-slate-400' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.total || 0),
            React.createElement('p', { className: 'text-xs text-slate-400' }, 'Gesamt')
          )
        )
      )
    ),
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'sent' ? 'ring-2 ring-blue-500' : ''),
      onClick: function() { setFilter('sent'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(Send, { className: 'h-5 w-5 text-blue-400' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.sent || 0),
            React.createElement('p', { className: 'text-xs text-blue-400' }, 'Gesendet')
          )
        )
      )
    ),
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'delivered' ? 'ring-2 ring-green-500' : ''),
      onClick: function() { setFilter('delivered'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(Check, { className: 'h-5 w-5 text-green-400' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.delivered || 0),
            React.createElement('p', { className: 'text-xs text-green-400' }, 'Zugestellt')
          )
        )
      )
    ),
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'opened' ? 'ring-2 ring-emerald-500' : ''),
      onClick: function() { setFilter('opened'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(Eye, { className: 'h-5 w-5 text-emerald-400' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.opened || 0),
            React.createElement('p', { className: 'text-xs text-emerald-400' }, 'Geöffnet')
          )
        )
      )
    ),
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'clicked' ? 'ring-2 ring-cyan-500' : ''),
      onClick: function() { setFilter('clicked'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(MousePointer, { className: 'h-5 w-5 text-cyan-400' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.clicked || 0),
            React.createElement('p', { className: 'text-xs text-cyan-400' }, 'Geklickt')
          )
        )
      )
    ),
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'bounced' ? 'ring-2 ring-red-500' : ''),
      onClick: function() { setFilter('bounced'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(AlertOctagon, { className: 'h-5 w-5 text-red-400' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.bounced || 0),
            React.createElement('p', { className: 'text-xs text-red-400' }, 'Zurückgewiesen')
          )
        )
      )
    ),
    React.createElement(Card, { 
      className: 'bg-slate-800 border-slate-700 cursor-pointer hover:border-slate-500 transition-colors ' + (filter === 'failed' ? 'ring-2 ring-red-600' : ''),
      onClick: function() { setFilter('failed'); }
    },
      React.createElement(CardContent, { className: 'p-4' },
        React.createElement('div', { className: 'flex items-center gap-3' },
          React.createElement(XCircle, { className: 'h-5 w-5 text-red-500' }),
          React.createElement('div', null,
            React.createElement('p', { className: 'text-2xl font-bold text-white' }, stats.failed || 0),
            React.createElement('p', { className: 'text-xs text-red-500' }, 'Fehlgeschlagen')
          )
        )
      )
    )
  );

  // Message rows
  var messageRows = messages.map(function(msg, index) {
    var statusConfig = MSG_STATUS_CONFIG[msg.message_status] || MSG_STATUS_CONFIG['pending'];
    var StatusIcon = statusConfig.icon;
    var isFailed = msg.message_status === 'failed' || msg.message_status === 'bounced';
    
    return React.createElement('tr', { 
      key: msg.id, 
      className: 'border-b border-slate-700 hover:bg-slate-800/50 cursor-pointer',
      onClick: function() { handleOpenDetail(msg.id); },
      'data-testid': 'mail-log-row-' + index
    },
      // Date
      React.createElement('td', { className: 'px-4 py-3 text-slate-400 text-sm whitespace-nowrap' },
        new Date(msg.created_at).toLocaleString('de-DE', { 
          day: '2-digit', month: '2-digit', year: 'numeric',
          hour: '2-digit', minute: '2-digit'
        })
      ),
      // Recipient
      React.createElement('td', { className: 'px-4 py-3' },
        React.createElement('div', { className: 'flex flex-col' },
          React.createElement('span', { className: 'text-white text-sm' }, msg.to_email),
          msg.customer_name && React.createElement('span', { className: 'text-slate-400 text-xs flex items-center gap-1 mt-0.5' },
            React.createElement(User, { className: 'h-3 w-3' }),
            msg.customer_name,
            msg.customer_company && React.createElement('span', { className: 'flex items-center gap-1' },
              ' · ',
              React.createElement(Building2, { className: 'h-3 w-3' }),
              msg.customer_company
            )
          )
        )
      ),
      // Subject
      React.createElement('td', { className: 'px-4 py-3' },
        React.createElement('span', { className: 'text-slate-300 text-sm truncate block max-w-xs' }, msg.subject)
      ),
      // Type/Source
      React.createElement('td', { className: 'px-4 py-3' },
        msg.source === 'support' ?
          React.createElement('div', { className: 'flex flex-col gap-1' },
            React.createElement(Badge, { className: 'bg-blue-500/20 text-blue-400 text-xs w-fit' },
              React.createElement(MessageSquare, { className: 'h-3 w-3 mr-1' }),
              'Support'
            ),
            msg.ticket_number && React.createElement(Link, { 
              to: '/admin/tickets/' + msg.ticket_id,
              onClick: function(e) { e.stopPropagation(); },
              className: 'text-xs text-slate-400 hover:text-blue-400'
            }, msg.ticket_number)
          ) :
          React.createElement('div', { className: 'flex flex-col gap-1' },
            React.createElement(Badge, { className: 'bg-purple-500/20 text-purple-400 text-xs w-fit' },
              React.createElement(Settings, { className: 'h-3 w-3 mr-1' }),
              'System'
            ),
            msg.tag && React.createElement('span', { className: 'text-xs text-slate-500' }, msg.tag)
          )
      ),
      // Status
      React.createElement('td', { className: 'px-4 py-3' },
        React.createElement('div', { className: 'flex items-center gap-2' },
          React.createElement(Badge, { className: statusConfig.className + ' text-xs' },
            React.createElement(StatusIcon, { className: 'h-3 w-3 mr-1' }),
            statusConfig.label
          ),
          isFailed && React.createElement(RotateCcw, { className: 'h-4 w-4 text-red-400', title: 'Erneut senden' })
        )
      ),
      // Actions
      React.createElement('td', { className: 'px-4 py-3' },
        React.createElement('div', { className: 'flex items-center gap-1' },
          msg.postal_raw_id && React.createElement(Button, {
            variant: 'ghost',
            size: 'sm',
            onClick: function(e) { e.stopPropagation(); handleRefreshSingle(msg.id); },
            className: 'text-slate-400 hover:text-white h-8 w-8 p-0'
          },
            React.createElement(RefreshCw, { className: 'h-4 w-4' })
          ),
          React.createElement(Button, {
            variant: 'ghost',
            size: 'sm',
            onClick: function(e) { e.stopPropagation(); handleOpenDetail(msg.id); },
            className: 'text-slate-400 hover:text-white h-8 w-8 p-0'
          },
            React.createElement(Eye, { className: 'h-4 w-4' })
          )
        )
      )
    );
  });

  if (loading) {
    return React.createElement('div', { className: 'flex items-center justify-center py-20' },
      React.createElement(Loader2, { className: 'h-8 w-8 animate-spin text-blue-500' })
    );
  }

  return React.createElement('div', { className: 'space-y-6', 'data-testid': 'admin-mail-log' },
    // Header
    React.createElement('div', { className: 'flex items-center justify-between' },
      React.createElement('div', null,
        React.createElement('h1', { className: 'text-2xl font-bold text-white' }, 'E-Mail Log'),
        React.createElement('p', { className: 'text-slate-400 mt-1' }, 'Alle ausgehenden E-Mails mit Zustellstatus')
      ),
      React.createElement('div', { className: 'flex items-center gap-3' },
        React.createElement('span', { className: 'text-xs text-slate-500' }, 'Auto-Refresh (30s)'),
        React.createElement(Button, {
          variant: 'outline',
          size: 'sm',
          onClick: handleRefreshAll,
          disabled: refreshing,
          className: 'border-slate-600 text-slate-300'
        },
          refreshing ? 
            React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) :
            React.createElement(RefreshCw, { className: 'h-4 w-4 mr-2' }),
          'Status aktualisieren'
        )
      )
    ),
    
    // Stats Cards
    statsCards,
    
    // Mail Table
    React.createElement(Card, { className: 'bg-slate-800 border-slate-700' },
      React.createElement(CardContent, { className: 'p-0' },
        React.createElement('div', { className: 'overflow-x-auto' },
          React.createElement('table', { className: 'w-full' },
            React.createElement('thead', null,
              React.createElement('tr', { className: 'border-b border-slate-700 bg-slate-800/50' },
                React.createElement('th', { className: 'px-4 py-3 text-left text-slate-400 text-xs font-medium uppercase tracking-wider' }, 'Datum'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-slate-400 text-xs font-medium uppercase tracking-wider' }, 'Empfänger'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-slate-400 text-xs font-medium uppercase tracking-wider' }, 'Betreff'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-slate-400 text-xs font-medium uppercase tracking-wider' }, 'Typ'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-slate-400 text-xs font-medium uppercase tracking-wider' }, 'Status'),
                React.createElement('th', { className: 'px-4 py-3 text-left text-slate-400 text-xs font-medium uppercase tracking-wider w-24' }, 'Aktion')
              )
            ),
            React.createElement('tbody', null,
              messages.length === 0 ?
                React.createElement('tr', null,
                  React.createElement('td', { colSpan: 6, className: 'px-4 py-12 text-center text-slate-500' },
                    'Keine E-Mails gefunden'
                  )
                ) :
                messageRows
            )
          )
        )
      )
    ),
    
    // Detail Dialog
    React.createElement(Dialog, { open: showDetailDialog, onOpenChange: setShowDetailDialog },
      React.createElement(DialogContent, { className: 'bg-slate-800 border-slate-700 text-white max-w-4xl max-h-[90vh]' },
        React.createElement(DialogHeader, null,
          React.createElement(DialogTitle, { className: 'text-white flex items-center gap-2' },
            React.createElement(Mail, { className: 'h-5 w-5' }),
            'E-Mail Details'
          ),
          selectedMessage && React.createElement(DialogDescription, { className: 'text-slate-400' },
            selectedMessage.subject
          )
        ),
        loadingDetail ? 
          React.createElement('div', { className: 'flex justify-center py-12' },
            React.createElement(Loader2, { className: 'h-8 w-8 animate-spin text-primary' })
          ) :
          selectedMessage && React.createElement('div', { className: 'space-y-4' },
            // Meta info
            React.createElement('div', { className: 'grid grid-cols-2 gap-4 text-sm' },
              React.createElement('div', null,
                React.createElement('span', { className: 'text-slate-400' }, 'An: '),
                React.createElement('span', { className: 'text-white' }, selectedMessage.to_email)
              ),
              React.createElement('div', null,
                React.createElement('span', { className: 'text-slate-400' }, 'Von: '),
                React.createElement('span', { className: 'text-white' }, selectedMessage.from_email)
              ),
              React.createElement('div', null,
                React.createElement('span', { className: 'text-slate-400' }, 'Status: '),
                React.createElement(Badge, { className: (MSG_STATUS_CONFIG[selectedMessage.message_status] || MSG_STATUS_CONFIG['pending']).className },
                  (MSG_STATUS_CONFIG[selectedMessage.message_status] || MSG_STATUS_CONFIG['pending']).label
                )
              ),
              React.createElement('div', null,
                React.createElement('span', { className: 'text-slate-400' }, 'Datum: '),
                React.createElement('span', { className: 'text-white' }, 
                  selectedMessage.created_at ? new Date(selectedMessage.created_at).toLocaleString('de-DE') : '-'
                )
              )
            ),
            // Error message if failed
            selectedMessage.error && React.createElement('div', { className: 'p-3 rounded bg-red-500/10 border border-red-500/30' },
              React.createElement('p', { className: 'text-sm text-red-400 font-medium' }, 'Fehler:'),
              React.createElement('p', { className: 'text-sm text-red-300' }, selectedMessage.error)
            ),
            // HTML content preview
            React.createElement('div', { className: 'space-y-2' },
              React.createElement('div', { className: 'flex items-center justify-between' },
                React.createElement('span', { className: 'text-slate-400 text-sm flex items-center gap-2' },
                  React.createElement(FileText, { className: 'h-4 w-4' }),
                  'Inhalt:'
                )
              ),
              React.createElement(ScrollArea, { className: 'h-[50vh] rounded border border-slate-600 bg-white p-4' },
                selectedMessage.html_body ?
                  React.createElement('div', { 
                    className: 'text-sm',
                    dangerouslySetInnerHTML: { __html: selectedMessage.html_body }
                  }) :
                  React.createElement('pre', { className: 'text-sm text-slate-800 whitespace-pre-wrap' },
                    selectedMessage.plain_body || 'Kein Inhalt'
                  )
              )
            ),
            // Resend button for failed emails
            (selectedMessage.message_status === 'failed' || selectedMessage.message_status === 'bounced') && 
              React.createElement('div', { className: 'flex justify-end pt-4 border-t border-slate-700' },
                React.createElement(Button, {
                  onClick: function() { handleResendEmail(selectedMessage.id); },
                  disabled: resending,
                  className: 'bg-blue-600 hover:bg-blue-700'
                },
                  resending ? 
                    React.createElement(Loader2, { className: 'h-4 w-4 mr-2 animate-spin' }) :
                    React.createElement(RotateCcw, { className: 'h-4 w-4 mr-2' }),
                  'Erneut senden'
                )
              )
          )
      )
    )
  );
};
